/**
 * Order Selectors
 * Accesses order data from Redux store.
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, selectors ONLY access data, they do NOT calculate.
 * All order operations are handled by backend Order Service.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Select all orders
export const selectAllOrders = createSelector(
  [(state: RootState) => state.orders.orders],
  (orders) => orders
);

// Select orders by ID (normalized)
export const selectOrderById = createSelector(
  [(state: RootState) => state.orders.byId, (_state: RootState, orderId: string) => orderId],
  (byId, orderId) => byId[orderId] || null
);

// Select orders by status
export const selectOrdersByStatus = createSelector(
  [(state: RootState) => state.orders.orders, (_state: RootState, status: string) => status],
  (orders, status) => orders.filter((order) => order.status === status)
);

// Select orders by broker account
export const selectOrdersByBrokerAccount = createSelector(
  [(state: RootState) => state.orders.orders, (_state: RootState, brokerAccountId: string) => brokerAccountId],
  (orders, brokerAccountId) => orders.filter((order) => order.broker_account_id === brokerAccountId)
);

// Select orders by instrument
export const selectOrdersByInstrument = createSelector(
  [(state: RootState) => state.orders.orders, (_state: RootState, instrumentId: string) => instrumentId],
  (orders, instrumentId) => orders.filter((order) => order.instrument_id === instrumentId)
);

// Select pending orders
export const selectPendingOrders = createSelector(
  [(state: RootState) => state.orders.orders],
  (orders) => orders.filter((order) => order.status === 'pending' || order.status === 'PENDING')
);

// Select executed orders
export const selectExecutedOrders = createSelector(
  [(state: RootState) => state.orders.orders],
  (orders) => orders.filter((order) => order.status === 'filled' || order.status === 'executed' || order.status === 'FILLED' || order.status === 'EXECUTED')
);

// Select failed orders
export const selectFailedOrders = createSelector(
  [(state: RootState) => state.orders.orders],
  (orders) => orders.filter((order) => order.status === 'rejected' || order.status === 'cancelled' || order.status === 'REJECTED' || order.status === 'CANCELLED')
);

// Select loading state
export const selectOrdersLoading = createSelector(
  [(state: RootState) => state.orders.loading],
  (loading) => loading
);

// Select error state
export const selectOrdersError = createSelector(
  [(state: RootState) => state.orders.error],
  (error) => error
);

// Select placing order state
export const selectPlacingOrder = createSelector(
  [(state: RootState) => state.orders.placingOrder],
  (placingOrder) => placingOrder
);

// Select placing order progress
export const selectPlacingOrderProgress = createSelector(
  [(state: RootState) => state.orders.placingOrderProgress],
  (progress) => progress
);

